<script setup>
import SuperAdminLink from './SuperAdminLink.vue';
import { useTalkviaBrand } from './useTalkviaBrand';
import DashboardFullscreenButton from './DashboardFullscreenButton.vue';
import { useSidebarViewport } from './useSidebarViewport';
import { h, ref, computed, onMounted, watch } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useMapGetter } from 'dashboard/composables/store';
import { useFavoriteInboxes } from 'dashboard/composables/useFavoriteInboxes';
import { useFavoriteLabels } from 'dashboard/composables/useFavoriteLabels';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import IxcInvoiceNotificationDeliveriesAPI from 'dashboard/api/ixcInvoiceNotificationDeliveries';
import { useEmailSync } from 'dashboard/composables/useEmailSync';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarGroupSeparator from './SidebarGroupSeparator.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import GameCompanion from 'dashboard/routes/dashboard/gamification/GameCompanion.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import SidebarCustomizationDialog from './SidebarCustomizationDialog.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import HorizontalNavigation from './HorizontalNavigation.vue';
import VerticalNavigationRail from './VerticalNavigationRail.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';
import AppearanceSettingsModal from './AppearanceSettingsModal.vue';
import MobileNavigationMenu from './MobileNavigationMenu.vue';
import {
  SIDEBAR_SORT_SECTIONS,
  getSidebarSortOptions,
  resolveSidebarSort,
  sortSidebarItems,
} from 'dashboard/helper/sidebarSort';
import {
  excludeFavoriteMenuItems,
  orderSidebarSections,
  resolveFavoriteMenuItems,
  uniqueSidebarNames,
} from 'dashboard/helper/sidebarCustomization';
import {
  CONNECTION_ACTIVE_ROUTES,
  INTEGRATION_ACTIVE_ROUTES,
  organizeDesktopMenuSections,
} from './managementMenu';
import { canAccessWhatsappCatalogs } from 'dashboard/routes/dashboard/whatsappCatalogs/catalogUtils';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
  layoutMode: {
    type: String,
    default: 'vertical',
  },
  containerMode: {
    type: String,
    default: 'full',
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnTalkviaCloud } = useAccount();
const { isEnterprise } = useConfig();
const store = useStore();
const { t } = useI18n();
const talkviaBrand = useTalkviaBrand();
const closeMenuLabel = 'Fechar menu';

const isCallsAvailable = computed(() => isOnTalkviaCloud.value || isEnterprise);

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');
const currentUser = useMapGetter('getCurrentUser');
const userAccounts = useMapGetter('getUserAccounts');
const currentRole = useMapGetter('getCurrentRole');
const notificationMetadata = useMapGetter('notifications/getMeta');
const horizontalUnreadCount = computed(() => {
  const count = notificationMetadata.value?.unreadCount || 0;
  if (!count) return '';
  return count < 100 ? String(count) : '99+';
});

const { width: windowWidth } = useWindowSize();
const mobileViewportStyle = useSidebarViewport();
const isMobile = computed(() => windowWidth.value < 768);
const isHorizontalDesktop = computed(
  () => props.layoutMode === 'horizontal' && windowWidth.value >= 1280
);
const isBoxedContainer = computed(() => props.containerMode === 'boxed');
const hasMultipleAccounts = computed(() => userAccounts.value.length > 1);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const isAdmin = computed(
  () => isSuperAdmin.value || currentRole.value === 'administrator'
);
const canAccessTalkviaShop = computed(
  () => isSuperAdmin.value || currentRole.value === 'administrator'
);
const accountId = useMapGetter('getCurrentAccountId');
const hasActiveIxcIntegration = ref(false);
const currentAccountMembership = computed(() =>
  currentUser.value?.accounts?.find(
    account => Number(account.id) === Number(accountId.value)
  )
);
const hasCurrentAccountPermission = permission =>
  currentAccountMembership.value?.permissions?.includes(permission) || false;
const canAccessCampaignsAndData = computed(
  () => isAdmin.value || hasCurrentAccountPermission('campaign_manage')
);
const canAccessAgentBots = computed(
  () => isAdmin.value || hasCurrentAccountPermission('agent_bot_manage')
);
const canManageFinance = computed(
  () => isAdmin.value || hasCurrentAccountPermission('finance_manage')
);
const canAccessBilling = computed(
  () => isAdmin.value || hasCurrentAccountPermission('billing_manage')
);
// Mesma política da API de Flows: administradores ou perfis com inbox_manage.
const canManageWhatsappFlows = computed(
  () => isAdmin.value || hasCurrentAccountPermission('inbox_manage')
);
const canAccessLemit = computed(
  () => isAdmin.value || currentAccountMembership.value?.lemit_access
);
const currentUserId = useMapGetter('getCurrentUserID');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const hasFilteredUnreadCounts = computed(() => {
  return (
    hasConversationUnreadCounts.value &&
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.UNREAD_COUNT_FOR_FILTERS
    )
  );
});

const hasAccountFeature = feature =>
  isFeatureEnabledonAccount.value(accountId.value, feature);

const hasFeature = feature => isSuperAdmin.value || hasAccountFeature(feature);

const hasIxcInvoiceNotificationsFeature = computed(() =>
  hasAccountFeature(FEATURE_FLAGS.TALKVIA_PROVIDER_IXC)
);

const hasCorbanFactaOffline = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_FACTA_OFFLINE)
);

const hasCorbanCltFgtsQuery = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_CLT_FGTS_QUERY)
);

const hasCorbanCreditEngine = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_CREDIT_ENGINE)
);

const hasCorbanManualSimulators = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_MANUAL_SIMULATORS)
);

const hasCorbanProposalPipeline = computed(() =>
  hasFeature(FEATURE_FLAGS.TALKVIA_CORBAN_PROPOSAL_PIPELINE)
);

const hasLogisticsInventoryRoutes = computed(() =>
  hasAccountFeature(FEATURE_FLAGS.TALKVIA_LOGISTICS_INVENTORY_ROUTES)
);

const hasCorbanModule = computed(
  () =>
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_FACTA_OFFLINE) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_CLT_FGTS_QUERY) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_BENEFIT360) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_CREDIT_ENGINE) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_MANUAL_SIMULATORS) ||
    hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_PROPOSAL_PIPELINE)
);

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const fetchSidebarSortPreferences = ([currentAccountId, userId]) => {
  if (!currentAccountId || !userId) return;
  store.dispatch('sidebarSortPreferences/initialize');
};

const fetchIxcIntegrationStatus = async ([currentAccountId, hasIxcFeature]) => {
  hasActiveIxcIntegration.value = false;
  if (!currentAccountId || !hasIxcFeature) return;

  try {
    const { data } = await IxcInvoiceNotificationDeliveriesAPI.status();
    if (Number(accountId.value) === Number(currentAccountId)) {
      hasActiveIxcIntegration.value = data?.enabled === true;
    }
  } catch {
    hasActiveIxcIntegration.value = false;
  }
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);
const verticalShellWidth = computed(() =>
  isEffectivelyCollapsed.value ? 64 : sidebarWidth.value + 64
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

const toggleSidebarCollapse = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const labels = useMapGetter('labels/getLabelsOnSidebar');
const inboxes = useMapGetter('inboxes/getInboxes');
const hasWhatsappCloudInbox = computed(() =>
  inboxes.value.some(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
  )
);
const emailInboxes = computed(() =>
  inboxes.value
    .filter(inbox => inbox.channel_type === 'Channel::Email')
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
);
const hasEmailInbox = computed(() => emailInboxes.value.length > 0);
const { isSyncing: isEmailSyncing, syncEmails } = useEmailSync();
const { favoriteInboxIds } = useFavoriteInboxes();
const { favoriteLabelIds } = useFavoriteLabels();
const favoriteInboxes = computed(() => {
  const inboxesById = new Map(
    inboxes.value.map(inbox => [Number(inbox.id), inbox])
  );
  return favoriteInboxIds.value
    .map(inboxId => inboxesById.get(inboxId))
    .filter(Boolean);
});
const favoriteLabels = computed(() => {
  const labelsById = new Map(
    labels.value.map(label => [Number(label.id), label])
  );

  return favoriteLabelIds.value
    .map(labelId => labelsById.get(labelId))
    .filter(Boolean);
});
const allUnreadCount = useMapGetter(
  'conversationUnreadCounts/getAllUnreadCount'
);
const getTeamUnreadCount = useMapGetter(
  'conversationUnreadCounts/getTeamUnreadCount'
);
const mentionsUnreadCount = useMapGetter(
  'conversationUnreadCounts/getMentionsUnreadCount'
);
const participatingUnreadCount = useMapGetter(
  'conversationUnreadCounts/getParticipatingUnreadCount'
);
const unattendedUnreadCount = useMapGetter(
  'conversationUnreadCounts/getUnattendedUnreadCount'
);
const getFolderUnreadCount = useMapGetter(
  'conversationUnreadCounts/getFolderUnreadCount'
);
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const getSidebarSectionSort = useMapGetter(
  'sidebarSortPreferences/getSectionSort'
);

onMounted(() => {
  // The sidebar must not disappear when the browser cache/IndexedDB stalls.
  store.dispatch('labels/getFromNetwork');
  store.dispatch('inboxes/getFromNetwork');
  store.dispatch('conversationOpenCounts/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

watch([accountId, currentUserId], fetchSidebarSortPreferences, {
  immediate: true,
});

watch(
  [accountId, hasIxcInvoiceNotificationsFeature],
  fetchIxcIntegrationStatus,
  { immediate: true }
);

const hasUnreadCountsForSection = section => {
  if (section === SIDEBAR_SORT_SECTIONS.FOLDERS) {
    return hasFilteredUnreadCounts.value;
  }

  return hasConversationUnreadCounts.value;
};

const getSortOptionsForSection = section =>
  getSidebarSortOptions(section, {
    hasUnreadCounts: hasUnreadCountsForSection(section),
  });

const getSortForSection = section =>
  resolveSidebarSort(section, getSidebarSectionSort.value(section), {
    hasUnreadCounts: hasUnreadCountsForSection(section),
  });

const updateSortPreference = (section, sortBy) => {
  store.dispatch('sidebarSortPreferences/setSectionSort', {
    section,
    sortBy,
  });
};

const buildSortConfig = section => ({
  sortOptions: getSortOptionsForSection(section),
  activeSort: getSortForSection(section),
  onSortChange: sortBy => updateSortPreference(section, sortBy),
});

const sortedFolders = computed(() =>
  sortSidebarItems(conversationCustomViews.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.FOLDERS),
    labelKey: view => view.name,
    unreadCountKey: view => getFolderUnreadCount.value(view.id),
  })
);

const sortedTeams = computed(() =>
  sortSidebarItems(teams.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.TEAMS),
    labelKey: team => team.name,
    unreadCountKey: team => getTeamUnreadCount.value(team.id),
  })
);

const unreadBadgeTooltips = computed(() => ({
  ALL: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.ALL'),
  INBOX: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.INBOX'),
  LABEL: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.LABEL'),
  TEAM: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.TEAM'),
  FOLDER: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.FOLDER'),
  MENTIONS: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.MENTIONS'),
  PARTICIPATING: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.PARTICIPATING'),
  UNATTENDED: t('SIDEBAR.UNREAD_COUNT_TOOLTIP.UNATTENDED'),
}));

const unreadBadgeTooltip = type => unreadBadgeTooltips.value[type];

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    icon: 'i-hugeicons-user-circle',
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    icon: 'i-hugeicons-tags',
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    icon: 'i-hugeicons-inbox',
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    icon: 'i-hugeicons-user-multiple',
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());
const menuSection = (label, options = {}) => ({
  type: 'section',
  name: `section-${label}`,
  label,
  show: options.show ?? true,
});

const menuItems = computed(() => {
  return [
    {
      name: 'Home',
      label: 'Home',
      icon: 'i-hugeicons-home-01',
      activeOn: ['home'],
      to: accountScopedRoute('home'),
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-hugeicons-bubble-chat',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-hugeicons-chatting-01',
          badgeCount: allUnreadCount.value,
          badgeTooltip: unreadBadgeTooltip('ALL'),
          activeOn: ['talkvia_conversations', 'inbox_conversation'],
          to: accountScopedRoute('talkvia_conversations'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          icon: 'i-hugeicons-at-sign',
          badgeCount: hasFilteredUnreadCounts.value
            ? mentionsUnreadCount.value
            : 0,
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Participating',
          label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
          icon: 'i-hugeicons-user-check-01',
          badgeCount: hasFilteredUnreadCounts.value
            ? participatingUnreadCount.value
            : 0,
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          icon: 'i-hugeicons-clock-alert',
          badgeCount: hasFilteredUnreadCounts.value
            ? unattendedUnreadCount.value
            : 0,
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'AI Queue',
          label: 'Atendimento IA',
          icon: 'i-hugeicons-bot',
          activeOn: ['conversation_ai', 'conversation_through_ai'],
          to: accountScopedRoute('conversation_ai'),
        },
        {
          name: 'Emails',
          label: t('SIDEBAR.EMAIL_CONVERSATIONS'),
          icon: 'i-hugeicons-mail-01',
          activeOn: [
            'conversation_emails',
            'conversation_through_emails',
            'email_folder',
          ],
          show: hasEmailInbox.value,
          collapsible: true,
          showTreeLine: true,
          children: [
            {
              name: 'Emails Inbox',
              label: t('SIDEBAR.EMAIL_INBOX'),
              icon: 'i-hugeicons-inbox',
              activeOn: ['conversation_emails', 'conversation_through_emails'],
              to: accountScopedRoute('conversation_emails'),
            },
            {
              name: 'Emails Junk',
              label: t('SIDEBAR.EMAIL_JUNK'),
              icon: 'i-hugeicons-spam',
              to: accountScopedRoute('email_folder', { folder: 'junk' }),
            },
            {
              name: 'Emails Trash',
              label: t('SIDEBAR.EMAIL_TRASH'),
              icon: 'i-hugeicons-delete-02',
              to: accountScopedRoute('email_folder', { folder: 'trash' }),
            },
          ],
        },
        {
          // Cada caixa de e-mail abre só as próprias conversas; o botão do
          // cabeçalho antecipa a leitura IMAP de todas as caixas.
          name: 'Email Inboxes',
          label: t('SIDEBAR.EMAIL_INBOXES'),
          icon: 'i-hugeicons-mail-at-sign-01',
          activeOn: [
            'email_inbox_conversations',
            'conversation_through_email_inbox',
          ],
          show: hasEmailInbox.value,
          collapsible: true,
          showTreeLine: true,
          headerAction: {
            icon: 'i-hugeicons-refresh',
            label: t('EMAIL_SYNC.LABEL'),
            loading: isEmailSyncing(),
            onClick: () => syncEmails(),
          },
          children: emailInboxes.value.map(inbox => ({
            name: `Email Inbox ${inbox.id}`,
            label: inbox.name,
            icon: 'i-hugeicons-mail-01',
            activeOn: [
              'email_inbox_conversations',
              'conversation_through_email_inbox',
            ],
            to: accountScopedRoute('email_inbox_conversations', {
              inbox_id: inbox.id,
            }),
          })),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-hugeicons-folder-01',
          activeOn: ['conversations_through_folders'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.FOLDERS),
          collapsible: true,
          showTreeLine: true,
          children: sortedFolders.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            badgeCount: hasFilteredUnreadCounts.value
              ? getFolderUnreadCount.value(view.id)
              : 0,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-hugeicons-user-group',
          activeOn: ['conversations_through_team'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.TEAMS),
          collapsible: true,
          showTreeLine: true,
          children: sortedTeams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            badgeCount: getTeamUnreadCount.value(team.id),
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-hugeicons-mailbox-01',
          to: accountScopedRoute('conversation_filters', {
            filterType: 'inboxes',
          }),
        },
        ...favoriteInboxes.value.map(inbox => ({
          name: `Favorite inbox-${inbox.id}`,
          label: inbox.name,
          icon: h(ChannelIcon, {
            inbox,
            colored: true,
            useBrandIcon: true,
            class: 'size-4',
          }),
          class: 'ms-7',
          to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
          activeOn: ['inbox_dashboard'],
        })),
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-hugeicons-tag-01',
          to: accountScopedRoute('conversation_filters', {
            filterType: 'labels',
          }),
        },
        ...favoriteLabels.value.map(label => ({
          name: `Favorite label-${label.id}`,
          label: label.title,
          icon: h('span', {
            class: 'h-4 w-1 rounded-full',
            style: { backgroundColor: label.color },
          }),
          class: 'ms-7',
          to: accountScopedRoute('label_conversations', {
            label: label.title,
          }),
          activeOn: ['label_conversations', 'conversations_through_label'],
        })),
      ],
    },
    {
      name: 'Notifications',
      label: t('SIDEBAR.NOTIFICATIONS'),
      icon: 'i-hugeicons-notification-01',
      activeOn: ['notifications_index'],
      to: accountScopedRoute('notifications_index'),
      getterKeys: {
        badge: 'notifications/getHasUnreadNotifications',
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Incidentes',
      label: t('OPERATIONAL_INCIDENTS.TITLE'),
      icon: 'i-lucide-shield-alert',
      activeOn: ['operational_incidents'],
      to: accountScopedRoute('operational_incidents'),
      show: isAdmin.value,
    },
    {
      name: 'Conquistas',
      label: 'Minha jornada',
      icon: 'i-hugeicons-champion',
      activeOn: ['talkvia_gamification'],
      to: accountScopedRoute('talkvia_gamification'),
    },
    {
      name: 'Gerenciar conquistas',
      label: 'Gerenciar conquistas',
      icon: 'i-hugeicons-medal-01',
      activeOn: ['talkvia_gamification_admin'],
      to: accountScopedRoute('talkvia_gamification_admin'),
      show: isAdmin.value,
    },
    {
      name: 'Comunidade',
      label: 'Comunidade',
      icon: 'i-hugeicons-chatting-01',
      activeOn: ['talkvia_forum', 'talkvia_forum_topic'],
      to: accountScopedRoute('talkvia_forum'),
    },
    menuSection('Corban', { show: hasCorbanModule.value }),
    {
      name: 'Pulso CLT',
      label: 'Pulso CLT',
      icon: 'i-hugeicons-activity-01',
      activeOn: ['talkvia_credit_dashboard'],
      to: accountScopedRoute('talkvia_credit_dashboard'),
      show: isAdmin.value && hasCorbanModule.value,
    },
    {
      name: 'Motores de crédito',
      label: 'Motores de crédito',
      icon: 'i-hugeicons-bank',
      activeOn: ['talkvia_credit_engines'],
      to: accountScopedRoute('talkvia_credit_engines'),
      show: isSuperAdmin.value && hasCorbanCreditEngine.value,
    },
    {
      name: 'Simuladores manuais',
      label: 'Simuladores manuais',
      icon: 'i-hugeicons-calculator-01',
      activeOn: ['talkvia_manual_simulators'],
      to: accountScopedRoute('talkvia_manual_simulators'),
      show: isSuperAdmin.value && hasCorbanManualSimulators.value,
    },
    {
      name: 'Consulta Lote CLT',
      label: 'Consulta Lote CLT',
      icon: 'i-hugeicons-file-spreadsheet',
      activeOn: ['talkvia_facta_offline_batches'],
      to: accountScopedRoute('talkvia_facta_offline_batches'),
      show: isAdmin.value && hasCorbanFactaOffline.value,
    },
    {
      name: 'Consulta CLT',
      label: 'Consulta CLT',
      icon: 'i-hugeicons-file-search',
      activeOn: ['talkvia_connected_bank_queries'],
      to: accountScopedRoute('talkvia_connected_bank_queries'),
      show: isAdmin.value && hasCorbanCltFgtsQuery.value,
    },
    {
      name: 'Benefício 360',
      label: 'Benefício 360',
      icon: 'i-hugeicons-user-search-01',
      activeOn: [
        'talkvia_benefit360',
        'talkvia_benefit360_campaigns',
        'talkvia_benefit360_campaign_list',
        'talkvia_benefit360_settings',
      ],
      to: accountScopedRoute('talkvia_benefit360'),
      show:
        hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN_BENEFIT360) &&
        (isAdmin.value ||
          hasCurrentAccountPermission('agent') ||
          hasCurrentAccountPermission('benefit360_query')),
    },
    {
      name: 'Esteira de propostas',
      label: 'Esteira de propostas',
      icon: 'i-hugeicons-browser',
      activeOn: ['talkvia_proposals'],
      to: accountScopedRoute('talkvia_proposals'),
      show: hasCorbanProposalPipeline.value,
    },
    menuSection('Operação'),
    {
      name: 'Gestão de carteira',
      label: 'Gestão de carteira',
      icon: 'i-hugeicons-user-multiple',
      activeOn: ['talkvia_portfolio_management'],
      to: accountScopedRoute('talkvia_portfolio_management'),
      show: isAdmin.value,
    },
    {
      name: 'Cadastrar cliente',
      label: 'Cadastrar cliente',
      icon: 'i-hugeicons-user-add-01',
      to: accountScopedRoute(
        'contacts_dashboard_index',
        {},
        { page: 1, new_contact: 'true' }
      ),
    },
    {
      name: 'Kanban',
      label: 'Kanban',
      icon: 'i-hugeicons-kanban',
      activeOn: ['conversation_kanban'],
      to: accountScopedRoute('conversation_kanban'),
    },
    {
      name: 'Calls',
      label: t('SIDEBAR.CALLS'),
      icon: 'i-hugeicons-call',
      to: accountScopedRoute('calls_dashboard_index'),
      activeOn: ['calls_dashboard_index'],
      show: isCallsAvailable.value,
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-hugeicons-identity-card',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-hugeicons-dashboard-square-01',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          collapsible: true,
          showTreeLine: true,
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-hugeicons-tag-01',
          label: t('SIDEBAR.TAGGED_WITH'),
          collapsible: true,
          showTreeLine: true,
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Opt-in e opt-out',
      label: 'Opt-in e opt-out',
      icon: 'i-hugeicons-security-check',
      activeOn: ['contacts_consents'],
      to: accountScopedRoute('contacts_consents'),
      show: isAdmin.value,
    },
    {
      name: 'Novidades',
      label: 'Novidades',
      icon: 'i-hugeicons-notification-02',
      activeOn: ['platform_announcements'],
      to: accountScopedRoute('platform_announcements'),
    },
    {
      name: 'Contratos',
      label: 'Contratos',
      icon: 'i-hugeicons-signature',
      activeOn: ['talkvia_contracts'],
      to: accountScopedRoute('talkvia_contracts'),
    },
    {
      name: 'Fornecedores',
      label: 'Fornecedores',
      icon: 'i-lucide-building-2',
      activeOn: ['talkvia_suppliers'],
      to: accountScopedRoute('talkvia_suppliers'),
      show: canManageFinance.value,
    },
    {
      name: 'Contas a receber',
      label: 'Contas a receber',
      icon: 'i-hugeicons-money-receive-01',
      activeOn: ['talkvia_receivables'],
      to: accountScopedRoute('talkvia_receivables'),
      show: canManageFinance.value,
    },
    {
      name: 'Contas a pagar',
      label: 'Contas a pagar',
      icon: 'i-hugeicons-money-send-01',
      activeOn: ['talkvia_payables'],
      to: accountScopedRoute('talkvia_payables'),
      show: canManageFinance.value,
    },
    {
      name: 'Conciliação',
      label: 'Conciliação',
      icon: 'i-lucide-list-checks',
      activeOn: ['talkvia_reconciliation'],
      to: accountScopedRoute('talkvia_reconciliation'),
      show: canManageFinance.value,
    },
    {
      name: 'Captação de leads',
      label: 'Captação de leads',
      icon: 'i-hugeicons-bubble-chat-favourite',
      activeOn: ['qualifica_ai'],
      to: accountScopedRoute('qualifica_ai'),
      show: canAccessCampaignsAndData.value,
    },
    {
      // O nome técnico permanece por compatibilidade com favoritos salvos.
      name: 'Funil visual',
      label: t('SIDEBAR.VISUAL_AUTOMATIONS'),
      icon: 'i-hugeicons-workflow-square-01',
      activeOn: ['talkvia_funnels'],
      to: accountScopedRoute('talkvia_funnels'),
      show: isAdmin.value,
    },
    {
      name: 'WhatsApp Flows',
      label: t('WHATSAPP_FLOWS.MENU'),
      icon: 'i-hugeicons-workflow-square-06',
      activeOn: ['whatsapp_flows'],
      to: accountScopedRoute('whatsapp_flows'),
      show: canManageWhatsappFlows.value && hasWhatsappCloudInbox.value,
    },
    {
      name: 'Templates da Meta',
      label: t('META_TEMPLATES.MENU'),
      icon: 'i-hugeicons-message-edit-01',
      activeOn: ['meta_templates'],
      to: accountScopedRoute('meta_templates'),
      show: isAdmin.value,
    },
    {
      name: 'Catálogos do WhatsApp',
      label: t('WHATSAPP_CATALOGS.MENU'),
      icon: 'i-hugeicons-store-01',
      activeOn: ['whatsapp_catalogs'],
      to: accountScopedRoute('whatsapp_catalogs'),
      show: canAccessWhatsappCatalogs({
        isAdmin: isAdmin.value,
        permissions: currentAccountMembership.value?.permissions,
        inboxes: inboxes.value,
      }),
    },
    {
      name: 'Agenda',
      label: 'Agenda',
      icon: 'i-hugeicons-calendar-03',
      activeOn: ['talkvia_calendar'],
      to: accountScopedRoute('talkvia_calendar'),
    },
    {
      name: 'Agente de Marketing IA',
      label: 'Agente de Marketing IA',
      icon: 'i-hugeicons-ai-paintbrush',
      activeOn: ['talkvia_marketing_agent'],
      to: accountScopedRoute('talkvia_marketing_agent'),
      show: isAdmin.value && hasAccountFeature('talkvia_marketing_ai'),
    },
    {
      name: 'Talkvia Studio',
      label: 'Talkvia Studio',
      icon: 'i-hugeicons-scissor-01',
      activeOn: ['talkvia_studio'],
      to: accountScopedRoute('talkvia_studio'),
      show: hasAccountFeature('talkvia_studio'),
    },
    {
      name: 'Lanchonete',
      label: 'Talkvia Food',
      icon: 'i-hugeicons-menu-restaurant',
      activeOn: [
        'overview',
        'orders',
        'kitchen',
        'deliveries',
        'menu',
        'cashier',
        'settings',
      ].map(tab => `talkvia_restaurant_${tab}`),
      to: accountScopedRoute('talkvia_restaurant_overview'),
      show: hasAccountFeature('talkvia_restaurant'),
    },
    {
      name: 'Estoque',
      label: 'Estoque',
      icon: 'i-hugeicons-package',
      activeOn: ['talkvia_inventory'],
      to: accountScopedRoute('talkvia_inventory'),
      show:
        hasLogisticsInventoryRoutes.value ||
        hasAccountFeature('talkvia_restaurant'),
    },
    {
      name: 'Rotas',
      label: 'Rotas',
      icon: 'i-hugeicons-route-02',
      activeOn: ['talkvia_delivery_routes'],
      to: accountScopedRoute('talkvia_delivery_routes'),
      show: hasLogisticsInventoryRoutes.value,
    },
    {
      name: 'Marketplace',
      label: 'Marketplace',
      icon: 'i-hugeicons-shopping-bag-01',
      activeOn: ['talkvia_shop'],
      to: accountScopedRoute('talkvia_shop'),
      show: canAccessTalkviaShop.value,
    },
    {
      name: 'Indique e ganhe',
      label: 'Indique e ganhe',
      icon: 'i-hugeicons-gift',
      activeOn: ['talkvia_referrals'],
      to: accountScopedRoute('talkvia_referrals'),
      show: canAccessTalkviaShop.value,
    },
    {
      name: 'Higienização',
      label: 'Higienização',
      icon: 'i-hugeicons-contact-book',
      activeOn: ['talkvia_lemit'],
      to: accountScopedRoute('talkvia_lemit'),
      show: canAccessCampaignsAndData.value && canAccessLemit.value,
    },
    menuSection('Automação e conteúdo'),
    {
      name: 'SMS',
      label: 'Disparos SMS',
      icon: 'i-hugeicons-sent',
      activeOn: ['talkvia_sms'],
      to: accountScopedRoute('talkvia_sms'),
      show: canAccessCampaignsAndData.value,
    },
    {
      name: 'Integrations',
      label: t('SIDEBAR.INTEGRATIONS'),
      icon: 'i-hugeicons-plug-01',
      activeOn: INTEGRATION_ACTIVE_ROUTES,
      to: accountScopedRoute('settings_applications'),
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-hugeicons-megaphone-01',
      to: accountScopedRoute('campaigns_whatsapp_index'),
      show: canAccessCampaignsAndData.value,
    },
    {
      name: 'Email Campaigns',
      label: 'Campanhas de e-mail',
      icon: 'i-hugeicons-mail-01',
      activeOn: ['campaigns_email_index'],
      to: accountScopedRoute('campaigns_email_index'),
      show: canAccessCampaignsAndData.value,
    },
    {
      name: 'Biblioteca de IA',
      label: 'Biblioteca de IA',
      icon: 'i-hugeicons-library',
      activeOn: ['talkvia_openai'],
      to: accountScopedRoute('talkvia_openai'),
      show: isAdmin.value,
    },
    {
      name: 'Supervisão IA',
      label: 'Supervisão IA',
      icon: 'i-hugeicons-radar-01',
      activeOn: ['talkvia_ai_supervision'],
      to: accountScopedRoute('talkvia_ai_supervision'),
      show: isSuperAdmin.value,
    },
    {
      name: 'Captain',
      icon: 'i-woot-captain',
      label: t('SIDEBAR.CAPTAIN'),
      activeOn: ['captain_assistants_create_index'],
      show: isSuperAdmin.value,
      children: [
        {
          name: 'Overview',
          label: t('SIDEBAR.CAPTAIN_OVERVIEW'),
          activeOn: ['captain_assistants_overview_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_overview_index',
          }),
        },
        {
          name: 'FAQs',
          label: t('SIDEBAR.CAPTAIN_RESPONSES'),
          activeOn: [
            'captain_assistants_responses_index',
            'captain_assistants_faq_suggestions',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_responses_index',
          }),
        },
        {
          name: 'Documents',
          label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
          activeOn: ['captain_assistants_documents_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_documents_index',
          }),
        },
        {
          name: 'Scenarios',
          label: t('SIDEBAR.CAPTAIN_SCENARIOS'),
          activeOn: ['captain_assistants_scenarios_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_scenarios_index',
          }),
        },
        {
          name: 'Playground',
          label: t('SIDEBAR.CAPTAIN_PLAYGROUND'),
          activeOn: ['captain_assistants_playground_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_playground_index',
          }),
        },
        {
          name: 'Inboxes',
          label: t('SIDEBAR.CAPTAIN_INBOXES'),
          activeOn: ['captain_assistants_inboxes_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_inboxes_index',
          }),
        },
        {
          name: 'Tools',
          label: t('SIDEBAR.CAPTAIN_TOOLS'),
          activeOn: ['captain_tools_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_tools_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.CAPTAIN_SETTINGS'),
          activeOn: [
            'captain_assistants_settings_index',
            'captain_assistants_settings_system_index',
            'captain_assistants_settings_audience_index',
            'captain_assistants_settings_schedule_index',
            'captain_assistants_guidelines_index',
            'captain_assistants_guardrails_index',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-hugeicons-library',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Tour Talkvia',
      label: 'Manual e Tour',
      icon: 'i-hugeicons-maps',
      activeOn: ['talkvia_tour'],
      to: accountScopedRoute('talkvia_tour'),
    },
    menuSection('Gestão'),
    ...(hasActiveIxcIntegration.value
      ? [
          {
            name: 'IXC',
            label: 'IXCsoft',
            icon: 'i-hugeicons-router',
            children: [
              {
                name: 'Reports IXC',
                label: t('SIDEBAR.REPORTS_IXC'),
                icon: 'i-hugeicons-invoice-01',
                to: accountScopedRoute('ixc_invoice_notification_reports'),
              },
              {
                name: 'IXC Announcements',
                label: 'Comunicados',
                icon: 'i-hugeicons-megaphone-01',
                to: accountScopedRoute('ixc_announcements'),
                show: isAdmin.value,
              },
            ],
          },
        ]
      : []),
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-hugeicons-analytics-up',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          icon: 'i-hugeicons-dashboard-square-01',
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          icon: 'i-hugeicons-chatting-01',
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          icon: 'i-hugeicons-smile',
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          icon: 'i-hugeicons-timer-01',
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Meta Usage',
          label: 'Consumo Meta',
          icon: 'i-brand-meta',
          to: accountScopedRoute('whatsapp_usage_reports'),
          show: isAdmin.value,
        },
        {
          name: 'Reports AI Usage',
          label: 'Consumo de IA',
          icon: 'i-hugeicons-ai-magic',
          to: accountScopedRoute('ai_usage_reports'),
          show: isAdmin.value,
        },
        {
          name: 'Reports Agent Pauses',
          label: t('SIDEBAR.REPORTS_AGENT_PAUSES'),
          icon: 'i-hugeicons-coffee-01',
          to: accountScopedRoute('agent_pause_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          icon: 'i-hugeicons-bot',
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    {
      name: 'Visão geral',
      label: 'Visão geral',
      icon: 'i-hugeicons-dashboard-square-01',
      activeOn: ['settings_connections_home'],
      to: accountScopedRoute('settings_connections_home'),
    },
    {
      name: 'Connections',
      label: 'Conexões',
      icon: 'i-hugeicons-connect',
      activeOn: CONNECTION_ACTIVE_ROUTES,
      to: accountScopedRoute('settings_inbox_list'),
    },
    {
      name: 'Assistente IA',
      label: t('SIDEBAR.AGENT_BOTS'),
      icon: 'i-hugeicons-bot',
      activeOn: ['agent_bots'],
      to: accountScopedRoute('agent_bots'),
      show: canAccessAgentBots.value,
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-hugeicons-settings-01',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-hugeicons-building-03',
          to: accountScopedRoute('general_settings_index'),
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-hugeicons-user-circle',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-hugeicons-user-multiple',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-hugeicons-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-hugeicons-user-settings-01',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-hugeicons-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-hugeicons-playlist-01',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: 'Automação do atendimento',
          icon: 'i-hugeicons-refresh',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Agent Availability',
          label: 'Status de disponibilidade',
          icon: 'i-hugeicons-coffee-01',
          to: accountScopedRoute('agent_pauses_settings'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-hugeicons-puzzle',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-hugeicons-message-02',
          to: accountScopedRoute('canned_list'),
        },
        ...(hasWhatsappCloudInbox.value
          ? [
              {
                name: 'Settings Interactive Messages',
                label: t('SIDEBAR.INTERACTIVE_MESSAGES'),
                icon: 'i-hugeicons-message-01',
                to: accountScopedRoute('whatsapp_interactive_templates'),
              },
            ]
          : []),
        {
          name: 'Conversation Workflow',
          label: 'Follow-up',
          icon: 'i-hugeicons-git-branch',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-hugeicons-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-hugeicons-clipboard',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-hugeicons-shield-01',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings API Docs',
          label: t('SIDEBAR.API_DOCS'),
          icon: 'i-hugeicons-file-code',
          to: accountScopedRoute('api_docs_settings_index'),
        },
      ],
    },
    {
      name: 'Financeiro',
      label: 'Financeiro',
      icon: 'i-hugeicons-invoice-01',
      activeOn: ['billing_settings_index', 'billing_credits_index'],
      to: accountScopedRoute('billing_settings_index'),
      show: canAccessBilling.value,
    },
  ];
});

const favoriteItemNames = ref([]);
const collapsedSectionNames = ref([]);
const sectionOrderNames = ref([]);
const itemOrderNames = ref([]);
const isCustomizationOpen = ref(false);
defineExpose({
  openCustomization: () => {
    isCustomizationOpen.value = true;
  },
});
const showAppearanceModal = ref(false);
const isDarkTheme = ref(
  typeof document !== 'undefined' && document.body.classList.contains('dark')
);
const quickThemeLabel = computed(() =>
  isDarkTheme.value ? 'Ativar tema claro' : 'Ativar tema escuro'
);
const appearanceLabel = 'Personalizar aparência';
const favoritesSectionLabel = 'Favoritos';
const moduleNavigationLabel = 'Navegação do módulo';

const sidebarCustomizationStorageKey = computed(
  () =>
    `talkvia:sidebar-customization:v1:${currentUserId.value || 'anonymous'}:${accountId.value || 'default'}`
);

const refreshThemeState = () => {
  isDarkTheme.value = document.body.classList.contains('dark');
};

const toggleColorTheme = () => {
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.COLOR_SCHEME,
    isDarkTheme.value ? 'light' : 'dark'
  );
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
  refreshThemeState();
  window.dispatchEvent(new CustomEvent('appearance-preferences-changed'));
};

useEventListener(window, 'appearance-preferences-changed', refreshThemeState);

const loadSidebarCustomization = () => {
  if (typeof window === 'undefined') return;

  try {
    const savedState = JSON.parse(
      window.localStorage.getItem(sidebarCustomizationStorageKey.value) || '{}'
    );
    favoriteItemNames.value = Array.isArray(savedState.favorites)
      ? uniqueSidebarNames(savedState.favorites)
      : [];
    collapsedSectionNames.value = Array.isArray(savedState.collapsedSections)
      ? uniqueSidebarNames(savedState.collapsedSections).filter(
          name => name !== 'section-favorites'
        )
      : [];
    sectionOrderNames.value = uniqueSidebarNames(savedState.sectionOrder);
    itemOrderNames.value = uniqueSidebarNames(savedState.itemOrder);
  } catch {
    favoriteItemNames.value = [];
    collapsedSectionNames.value = [];
    sectionOrderNames.value = [];
    itemOrderNames.value = [];
  }
};

const saveSidebarCustomization = () => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      sidebarCustomizationStorageKey.value,
      JSON.stringify({
        favorites: favoriteItemNames.value,
        collapsedSections: collapsedSectionNames.value,
        sectionOrder: sectionOrderNames.value,
        itemOrder: itemOrderNames.value,
      })
    );
  } catch {
    // The menu remains usable when storage is disabled or full.
  }
};

const applySidebarCustomization = ({
  favorites,
  collapsedSections,
  sectionOrder,
  itemOrder,
}) => {
  favoriteItemNames.value = uniqueSidebarNames(favorites);
  collapsedSectionNames.value = uniqueSidebarNames(collapsedSections).filter(
    name => name !== 'section-favorites'
  );
  sectionOrderNames.value = uniqueSidebarNames(sectionOrder);
  itemOrderNames.value = uniqueSidebarNames(itemOrder);
  saveSidebarCustomization();
  isCustomizationOpen.value = false;
};

const toggleSection = sectionName => {
  collapsedSectionNames.value = collapsedSectionNames.value.includes(
    sectionName
  )
    ? collapsedSectionNames.value.filter(name => name !== sectionName)
    : [...collapsedSectionNames.value, sectionName];
  saveSidebarCustomization();
};

const isSectionCollapsed = sectionName =>
  collapsedSectionNames.value.includes(sectionName);

const menuSections = computed(() => {
  const sections = [];
  let currentSection;

  menuItems.value.forEach(item => {
    if (item.type === 'section') {
      currentSection = { ...item, items: [] };
      sections.push(currentSection);
      return;
    }

    if (!currentSection) {
      currentSection = {
        type: 'section',
        name: 'section-menu',
        label: '',
        items: [],
      };
      sections.push(currentSection);
    }
    if (item.show !== false) currentSection.items.push(item);
  });

  return sections.filter(section => section.items.length);
});

const favoriteItems = computed(() => {
  return resolveFavoriteMenuItems(menuItems.value, favoriteItemNames.value);
});

const orderedMenuSections = computed(() => {
  const sections = orderSidebarSections(
    menuSections.value,
    sectionOrderNames.value,
    itemOrderNames.value
  );
  const primarySection = sections.find(
    section => section.name === 'section-menu'
  );
  if (!primarySection) return sections;

  return [
    {
      ...primarySection,
      items: [...primarySection.items].sort((first, second) => {
        if (first.name === 'Home') return -1;
        if (second.name === 'Home') return 1;
        return 0;
      }),
    },
    ...sections.filter(section => section.name !== 'section-menu'),
  ];
});

const regularMenuSections = computed(() =>
  excludeFavoriteMenuItems(orderedMenuSections.value, favoriteItems.value)
);

const conversationsSectionName = 'section-conversations';
const activeVerticalSectionName = ref(conversationsSectionName);
const organizedModuleSections = computed(() =>
  organizeDesktopMenuSections(
    regularMenuSections.value.filter(
      section => section.name !== 'section-menu' && section.items.length
    ),
    {
      logisticsEnabled:
        hasLogisticsInventoryRoutes.value ||
        hasAccountFeature('talkvia_restaurant'),
    }
  )
);
const horizontalMenuSections = computed(() => {
  const primarySection = regularMenuSections.value.find(
    section => section.name === 'section-menu'
  );
  const modules = organizedModuleSections.value.map(section => ({
    ...section,
    items: [...section.items],
  }));

  return primarySection ? [primarySection, ...modules] : modules;
});
const verticalQuickItems = computed(() => {
  const primarySection = regularMenuSections.value.find(
    section => section.name === 'section-menu'
  );
  const homeItem = primarySection?.items.find(item => item.name === 'Home');
  const conversationItem = primarySection?.items.find(
    item => item.name === 'Conversation'
  );
  const allConversationsItem = conversationItem?.children?.find(
    item => item.name === 'All'
  );
  const marketplaceSection = organizedModuleSections.value.find(
    section => section.name === 'section-Marketplace'
  );
  const marketplaceItem = marketplaceSection?.items.find(
    item => item.name === 'Marketplace'
  );
  const achievementsItem = primarySection?.items.find(
    item => item.name === 'Conquistas'
  );
  const communityItem = primarySection?.items.find(
    item => item.name === 'Comunidade'
  );

  return [
    homeItem && {
      ...homeItem,
      name: 'Quick Home',
      sectionName: '',
    },
    allConversationsItem && {
      ...allConversationsItem,
      name: 'Quick Conversations',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: conversationItem.icon,
      sectionName: conversationsSectionName,
    },
    achievementsItem && {
      ...achievementsItem,
      name: 'Quick Achievements',
      sectionName: 'section-achievements',
    },
    communityItem && {
      ...communityItem,
      name: 'Quick Community',
      sectionName: '',
    },
    marketplaceItem && {
      ...marketplaceItem,
      name: 'Quick Marketplace',
      sectionName: marketplaceSection.name,
    },
  ].filter(Boolean);
});
// Celular: atalhos do Vertical com Notificações, que no desktop ficam no topo.
const mobileQuickItems = computed(() => {
  const primarySection = regularMenuSections.value.find(
    section => section.name === 'section-menu'
  );
  const notificationsItem = primarySection?.items.find(
    item => item.name === 'Notifications'
  );
  const quickItems = [...verticalQuickItems.value];
  if (notificationsItem) {
    const conversationsIndex = quickItems.findIndex(
      item => item.name === 'Quick Conversations'
    );
    quickItems.splice(conversationsIndex + 1, 0, {
      ...notificationsItem,
      name: 'Quick Notifications',
      sectionName: '',
    });
  }
  const order = [
    'Quick Home',
    'Quick Conversations',
    'Quick Notifications',
    'Quick Community',
    'Quick Marketplace',
    'Quick Achievements',
  ];
  return quickItems.sort(
    (first, second) => order.indexOf(first.name) - order.indexOf(second.name)
  );
});
const verticalMenuSections = computed(() => {
  const primarySection = regularMenuSections.value.find(
    section => section.name === 'section-menu'
  );
  const conversationItem = primarySection?.items.find(
    item => item.name === 'Conversation'
  );
  const primaryExtras = (primarySection?.items || []).filter(
    item =>
      ![
        'Home',
        'Conversation',
        'Comunidade',
        'Conquistas',
        'Gerenciar conquistas',
      ].includes(item.name)
  );
  const sections = organizedModuleSections.value.map(section => ({
    ...section,
    items: [...section.items],
  }));

  if (conversationItem) {
    sections.unshift({
      name: conversationsSectionName,
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: conversationItem.icon,
      items: [...conversationItem.children, ...primaryExtras],
    });
  }

  const achievementItems = (primarySection?.items || []).filter(item =>
    ['Conquistas', 'Gerenciar conquistas'].includes(item.name)
  );
  if (achievementItems.length) {
    sections.push({
      name: 'section-achievements',
      label: 'Conquistas',
      icon: 'i-hugeicons-champion',
      items: achievementItems,
    });
  }

  if (favoriteItems.value.length) {
    sections.unshift({
      name: 'section-favorites',
      label: favoritesSectionLabel,
      icon: 'i-hugeicons-star',
      items: favoriteItems.value,
    });
  }

  return sections;
});
const verticalRailSections = computed(() =>
  verticalMenuSections.value.filter(
    section =>
      ![
        conversationsSectionName,
        'section-Marketplace',
        'section-achievements',
      ].includes(section.name)
  )
);
const activeVerticalSection = computed(
  () =>
    verticalMenuSections.value.find(
      section => section.name === activeVerticalSectionName.value
    ) || verticalMenuSections.value[0]
);
const displayedRegularMenuSections = computed(() => {
  if (isMobile.value) return horizontalMenuSections.value;
  if (activeVerticalSection.value?.name === 'section-favorites') return [];
  return activeVerticalSection.value ? [activeVerticalSection.value] : [];
});

const selectVerticalSection = sectionName => {
  activeVerticalSectionName.value = sectionName;
};

watch(
  verticalMenuSections,
  sections => {
    if (
      !sections.some(
        section => section.name === activeVerticalSectionName.value
      )
    ) {
      activeVerticalSectionName.value =
        sections[0]?.name || conversationsSectionName;
    }
  },
  { immediate: true }
);

watch(sidebarCustomizationStorageKey, loadSidebarCustomization, {
  immediate: true,
});
</script>

<template>
  <aside
    v-if="isHorizontalDesktop"
    class="relative z-50 w-full shrink-0 rounded-2xl border border-n-weak bg-n-background shadow-sm"
  >
    <header class="h-16 border-b border-n-weak">
      <div
        class="mx-auto flex h-full items-center gap-3 px-4"
        :class="isBoxedContainer ? 'max-w-[1440px]' : 'w-full'"
      >
        <div class="flex shrink-0 items-center gap-2 pr-2">
          <span class="grid size-9 shrink-0 place-items-center">
            <img
              :src="talkviaBrand.icon"
              alt=""
              class="size-7 object-contain brightness-0 dark:brightness-100"
            />
          </span>
          <span class="leading-tight">
            <strong class="block text-sm font-bold text-n-slate-12">
              {{ talkviaBrand.name }}
            </strong>
            <small class="block text-[10px] text-n-slate-10">
              {{ talkviaBrand.tagline }}
            </small>
          </span>
        </div>

        <div v-if="hasMultipleAccounts" class="w-[min(17rem,24vw)] shrink-0">
          <SidebarAccountSwitcher
            class="w-full"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </div>

        <div class="flex-1" />

        <RouterLink
          v-tooltip.bottom="t('COMBOBOX.SEARCH_PLACEHOLDER')"
          :to="{ name: 'search' }"
          class="grid size-10 shrink-0 place-content-center rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-10 transition hover:border-n-brand/30 hover:bg-n-alpha-2 hover:text-n-brand"
          :aria-label="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-hugeicons-search-01 size-4" />
        </RouterLink>

        <SuperAdminLink v-if="isSuperAdmin" compact />

        <ComposeConversation align="end">
          <template #trigger="{ isOpen }">
            <Button
              icon="i-hugeicons-bubble-chat-add"
              label="Nova conversa"
              color="blue"
              size="md"
              class="!h-10 !rounded-xl"
              :class="isOpen && '!ring-2 !ring-n-brand/20'"
            />
          </template>
        </ComposeConversation>

        <DashboardFullscreenButton />

        <button
          v-tooltip.bottom="quickThemeLabel"
          type="button"
          class="grid size-10 shrink-0 place-content-center rounded-xl text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-brand"
          :aria-label="quickThemeLabel"
          @click="toggleColorTheme"
        >
          <span
            class="size-[1.125rem]"
            :class="isDarkTheme ? 'i-hugeicons-sun-03' : 'i-hugeicons-moon-02'"
          />
        </button>

        <button
          v-tooltip.bottom="appearanceLabel"
          type="button"
          class="grid size-10 shrink-0 place-content-center rounded-xl text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-brand"
          :aria-label="appearanceLabel"
          @click="showAppearanceModal = true"
        >
          <span class="i-hugeicons-paint-board size-[1.125rem]" />
        </button>

        <RouterLink
          v-tooltip.bottom="t('SIDEBAR.NOTIFICATIONS')"
          :to="accountScopedRoute('notifications_index')"
          class="relative grid size-10 shrink-0 place-content-center rounded-xl text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-brand"
          :aria-label="t('SIDEBAR.NOTIFICATIONS')"
        >
          <span class="i-hugeicons-notification-01 size-[1.125rem]" />
          <span
            v-if="horizontalUnreadCount"
            class="absolute right-0.5 top-0.5 grid min-h-4 min-w-4 place-items-center rounded-full bg-n-ruby-9 px-1 text-[9px] font-semibold leading-none text-white"
          >
            {{ horizontalUnreadCount }}
          </span>
        </RouterLink>

        <GameCompanion :account-id="accountId" :user-id="currentUserId" />
        <div class="shrink-0">
          <SidebarProfileMenu
            placement="topbar"
            @customize-navigation="isCustomizationOpen = true"
            @open-key-shortcut-modal="emit('openKeyShortcutModal')"
          />
        </div>
      </div>
    </header>

    <HorizontalNavigation
      :sections="horizontalMenuSections"
      :favorites="favoriteItems"
      :boxed="isBoxedContainer"
    />

    <SidebarCustomizationDialog
      :open="isCustomizationOpen"
      :sections="orderedMenuSections"
      :favorite-names="favoriteItemNames"
      :collapsed-section-names="collapsedSectionNames"
      @close="isCustomizationOpen = false"
      @save="applySidebarCustomization"
    />

    <AppearanceSettingsModal
      :show="showAppearanceModal"
      @close="showAppearanceModal = false"
      @customize-navigation="isCustomizationOpen = true"
    />
  </aside>

  <aside
    v-else
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="sidebar-shell fixed top-0 z-40 flex h-[100dvh] w-[min(360px,calc(100vw-16px))] flex-row overflow-hidden border-n-weak bg-n-background text-sm ltr:left-0 ltr:border-r rtl:right-0 rtl:border-l md:relative md:h-full md:w-auto md:flex-shrink-0 md:rounded-2xl md:border md:shadow-sm md:ltr:translate-x-0 md:rtl:translate-x-0"
    :class="[
      {
        'sidebar-shell--collapsed': isEffectivelyCollapsed,
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="
      isMobile
        ? {
            ...mobileViewportStyle,
            '--sidebar-visible-height': mobileViewportStyle.height,
          }
        : { width: `${verticalShellWidth}px` }
    "
    :inert="isMobile && !isMobileSidebarOpen"
  >
    <VerticalNavigationRail
      v-if="!isMobile"
      :sections="verticalRailSections"
      :route-sections="verticalMenuSections"
      :quick-items="verticalQuickItems"
      :show-super-admin="isSuperAdmin"
      :selected-section="activeVerticalSectionName"
      :panel-open="!isEffectivelyCollapsed"
      @toggle-panel="toggleSidebarCollapse"
      @select="selectVerticalSection"
    />

    <div
      v-show="isMobile || !isEffectivelyCollapsed"
      class="sidebar-panel relative grid h-full min-h-0 min-w-0 flex-1 grid-rows-[auto_minmax(0,1fr)_auto] bg-n-background"
    >
      <section class="mb-4 grid gap-2" :class="isMobile ? 'mt-1' : 'mt-5 px-1'">
        <div class="flex min-w-0 items-center gap-2 px-2">
          <div class="flex min-w-0 flex-1 items-center gap-2">
            <span
              class="grid size-9 shrink-0 place-items-center"
              :class="
                !isMobile &&
                'rounded-xl bg-n-brand text-sm font-bold text-white shadow-sm shadow-n-brand/20'
              "
            >
              <template v-if="isMobile"
                ><img
                  :src="talkviaBrand.icon"
                  alt=""
                  class="size-7 object-contain brightness-0 dark:brightness-100"
              /></template>
              <span
                v-else
                class="size-[1.125rem]"
                :class="activeVerticalSection?.icon"
              />
            </span>
            <span class="min-w-0 leading-tight">
              <strong class="block truncate text-sm font-bold text-n-slate-12">
                {{
                  isMobile ? talkviaBrand.name : activeVerticalSection?.label
                }}
              </strong>
              <small class="block truncate text-[10px] text-n-slate-10">
                {{ isMobile ? talkviaBrand.tagline : moduleNavigationLabel }}
              </small>
            </span>
          </div>
          <button
            v-if="isMobile"
            type="button"
            class="grid size-11 shrink-0 place-items-center rounded-xl text-n-slate-12 hover:bg-n-alpha-2"
            :aria-label="closeMenuLabel"
            @click="emit('closeMobileSidebar')"
          >
            <span class="i-hugeicons-cancel-01 size-5" aria-hidden="true" />
          </button>
        </div>
        <div
          v-if="isMobile"
          class="flex min-w-0 items-center gap-1 px-2"
          :class="{ 'justify-center': isEffectivelyCollapsed }"
        >
          <SidebarAccountSwitcher
            v-if="hasMultipleAccounts"
            :is-collapsed="isEffectivelyCollapsed"
            :class="
              isEffectivelyCollapsed
                ? ''
                : 'min-w-0 flex-1 rounded-xl border border-n-weak bg-n-solid-1 shadow-sm'
            "
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </div>
        <div
          v-if="isMobile"
          class="grid min-w-0"
          :class="
            isEffectivelyCollapsed
              ? 'grid-cols-1 justify-items-center gap-2'
              : 'grid-cols-[2.25rem_minmax(0,1fr)] mx-2 items-center overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-sm'
          "
        >
          <RouterLink
            v-if="!isEffectivelyCollapsed"
            v-tooltip.bottom="t('COMBOBOX.SEARCH_PLACEHOLDER')"
            :to="{ name: 'search' }"
            class="flex size-9 items-center justify-center border-r border-n-weak text-n-slate-11 transition-all duration-150 ease-out hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand dark:hover:bg-n-slate-9/30"
          >
            <span class="i-hugeicons-search-01 size-4" />
          </RouterLink>
          <RouterLink
            v-else
            v-tooltip.right="t('COMBOBOX.SEARCH_PLACEHOLDER')"
            :to="{ name: 'search' }"
            class="flex size-9 items-center justify-center border border-n-weak bg-n-alpha-1 text-n-slate-11 shadow-sm transition-all duration-150 ease-out hover:border-n-brand/50 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand dark:hover:bg-n-slate-9/30"
            :aria-label="t('COMBOBOX.SEARCH_PLACEHOLDER')"
          >
            <span class="i-hugeicons-search-01 size-4" />
          </RouterLink>
          <ComposeConversation align="start" trigger-class="min-w-0">
            <template #trigger="{ isOpen }">
              <Button
                v-tooltip.right="
                  isEffectivelyCollapsed ? 'Nova conversa' : null
                "
                icon="i-hugeicons-bubble-chat-add"
                :label="isEffectivelyCollapsed ? '' : 'Nova conversa'"
                color="slate"
                size="md"
                class="dark:hover:!bg-n-slate-9/30"
                :class="[
                  isEffectivelyCollapsed
                    ? '!size-9 !rounded-none !border !border-n-weak !bg-n-alpha-1 !p-0 !text-n-slate-11 !shadow-sm hover:!border-n-brand/50 hover:!bg-n-alpha-2'
                    : '!h-9 !w-full !min-w-0 !justify-center !rounded-none !border-0 !bg-transparent !px-2 !text-xs !font-medium !text-n-slate-12 !shadow-none hover:!bg-n-alpha-2',
                  {
                    '!border-n-brand !bg-n-alpha-2 dark:!bg-n-slate-9/30':
                      isOpen && isEffectivelyCollapsed,
                    '!bg-n-alpha-2': isOpen && !isEffectivelyCollapsed,
                  },
                ]"
              />
            </template>
          </ComposeConversation>
        </div>
      </section>
      <nav
        class="sidebar-scroll-region min-h-0 min-w-0 overflow-y-auto overscroll-contain pb-5"
        :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
      >
        <MobileNavigationMenu
          v-if="isMobile"
          class="pt-1"
          :quick-items="mobileQuickItems"
          :sections="verticalMenuSections"
          :show-super-admin="isSuperAdmin"
          :is-open="isMobileSidebarOpen"
          @navigate="closeMobileSidebar"
        />
        <ul
          v-else
          class="flex flex-col gap-1 m-0 list-none min-w-0"
          :class="{ 'items-center': isEffectivelyCollapsed }"
        >
          <li
            v-if="
              favoriteItems.length &&
              (isMobile || activeVerticalSectionName === 'section-favorites')
            "
            class="sidebar-favorites-sticky z-30 list-none bg-n-background pb-2"
            :class="isEffectivelyCollapsed ? 'pt-0' : 'pt-1'"
          >
            <SidebarGroupSeparator
              v-if="!isEffectivelyCollapsed"
              :label="favoritesSectionLabel"
            />
            <ul
              class="mt-1 flex min-w-0 list-none flex-col gap-1 p-0"
              :class="{ 'items-center': isEffectivelyCollapsed }"
            >
              <SidebarGroup
                v-for="item in favoriteItems"
                :key="`favorite-${item.name}`"
                v-bind="item"
                :class="{ 'ms-2 me-1': !isEffectivelyCollapsed }"
              />
            </ul>
          </li>
          <template
            v-for="section in displayedRegularMenuSections"
            :key="section.name"
          >
            <li
              v-if="isMobile && !isEffectivelyCollapsed && section.label"
              class="list-none pt-3 first:pt-1"
            >
              <SidebarGroupSeparator
                :label="section.label"
                :collapsible="isMobile"
                :is-expanded="
                  isMobile ? !isSectionCollapsed(section.name) : true
                "
                @toggle="isMobile && toggleSection(section.name)"
              />
            </li>
            <SidebarGroup
              v-for="item in section.items"
              v-show="!isMobile || !isSectionCollapsed(section.name)"
              :key="item.name"
              v-bind="item"
              :class="{
                'sticky top-0 z-20 bg-n-background pb-1':
                  item.name === 'Conversation',
                'ms-2 me-1': section.label && !isEffectivelyCollapsed,
              }"
            />
            <li
              v-if="
                isMobile &&
                isSuperAdmin &&
                section.name === 'section-menu' &&
                !isSectionCollapsed(section.name)
              "
              class="list-none px-2 py-1"
            >
              <SuperAdminLink />
            </li>
          </template>
        </ul>
      </nav>
      <section
        v-if="isMobile"
        class="sidebar-mobile-profile relative flex shrink-0 flex-col items-center justify-between gap-1 bg-n-background pb-[env(safe-area-inset-bottom)]"
      >
        <div
          class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
        />
        <SidebarChangelogCard
          v-if="
            isOnTalkviaCloud &&
            !isACustomBrandedInstance &&
            !isEffectivelyCollapsed
          "
        />
        <SidebarChangelogButton
          v-if="
            isOnTalkviaCloud &&
            !isACustomBrandedInstance &&
            isEffectivelyCollapsed
          "
        />
        <GameCompanion
          v-if="isEffectivelyCollapsed"
          :account-id="accountId"
          :user-id="currentUserId"
        />
        <div
          class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
          :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
        >
          <GameCompanion
            v-if="!isEffectivelyCollapsed"
            :account-id="accountId"
            :user-id="currentUserId"
          />
          <div :class="isEffectivelyCollapsed ? 'shrink-0' : 'min-w-0 flex-1'">
            <SidebarProfileMenu
              placement="mobile"
              :is-collapsed="isEffectivelyCollapsed"
              @customize-navigation="isCustomizationOpen = true"
              @open-key-shortcut-modal="emit('openKeyShortcutModal')"
            />
          </div>
          <router-link
            v-if="!isEffectivelyCollapsed"
            v-tooltip.left="t('SIDEBAR_ITEMS.PROFILE_SETTINGS')"
            :to="{ name: 'profile_settings_index' }"
            class="grid size-9 shrink-0 place-content-center rounded-lg border border-transparent text-n-slate-10 transition hover:border-n-weak hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
            :title="t('SIDEBAR_ITEMS.PROFILE_SETTINGS')"
            :aria-label="t('SIDEBAR_ITEMS.PROFILE_SETTINGS')"
            data-testid="sidebar-profile-settings"
            @click="closeMobileSidebar"
          >
            <span class="i-hugeicons-settings-01 size-4.5" />
          </router-link>
        </div>
      </section>
      <!-- Resize Handle (desktop only) -->
      <div
        class="group absolute top-0 z-40 hidden h-full w-1 cursor-col-resize md:block ltr:right-0 rtl:left-0"
        @mousedown="onResizeStart"
        @touchstart="onResizeStart"
        @dblclick="onResizeHandleDoubleClick"
      >
        <div
          class="absolute top-0 h-full w-px bg-transparent transition-colors group-hover:bg-n-brand ltr:right-0 rtl:left-0"
          :class="{ 'bg-n-brand': isResizing }"
        />
      </div>
    </div>
    <SidebarCustomizationDialog
      :open="isCustomizationOpen"
      :sections="orderedMenuSections"
      :favorite-names="favoriteItemNames"
      :collapsed-section-names="collapsedSectionNames"
      @close="isCustomizationOpen = false"
      @save="applySidebarCustomization"
    />
  </aside>
</template>

<style scoped>
.sidebar-scroll-region {
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}

.sidebar-shell--collapsed .sidebar-scroll-region {
  scrollbar-gutter: stable both-edges;
}

.sidebar-shell:hover .sidebar-scroll-region,
.sidebar-shell:focus-within .sidebar-scroll-region {
  scrollbar-color: rgba(120, 124, 136, 0.55) transparent;
}

.sidebar-favorites-sticky {
  position: sticky;
  top: 0;
}

.sidebar-scroll-region::-webkit-scrollbar {
  width: 6px;
}

.sidebar-scroll-region::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-scroll-region::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 999px;
}

.sidebar-shell:hover .sidebar-scroll-region::-webkit-scrollbar-thumb,
.sidebar-shell:focus-within .sidebar-scroll-region::-webkit-scrollbar-thumb {
  background: rgba(120, 124, 136, 0.55);
}

.sidebar-shell .sidebar-scroll-region::-webkit-scrollbar-thumb:hover {
  background: rgba(120, 124, 136, 0.8);
}
</style>
